import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import InstitutionNavbar from '../components/instituteNavbar';
import {
    Lock, Unlock, Mail, MapPin, User, ExternalLink
} from 'lucide-react';

const InvitedCandidates = () => {
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchInvitedCandidates();
    }, []);

    const fetchInvitedCandidates = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/institutionfilter/getInvitedCandidates`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCandidates(response.data.data);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching invited candidates:", err);
            setError("Failed to load invited candidates.");
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <InstitutionNavbar />
            <div className="pt-24 px-4 md:px-8 max-w-7xl mx-auto w-full">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                    <Mail className="text-yellow-600" /> Invited Candidates
                </h2>

                {loading && <div className="text-center py-10">Loading...</div>}
                {error && <div className="text-center py-10 text-red-500">{error}</div>}

                {!loading && !error && candidates.length === 0 && (
                    <div className="text-center py-10 text-gray-500">No invited candidates found.</div>
                )}

                <div className="grid grid-cols-1 gap-6">
                    {candidates.map((candidate) => {
                        const unlocked = candidate.is_unlocked; // Should be true generally for updates logic
                        const qualification = candidate.education?.length > 0 ? (candidate.education[0].degree || candidate.education[0].education) : "Not Specified";
                        const experienceVal = candidate.experience || candidate.work_preference?.experience || 0;


                        return (
                            <div key={candidate._id} className="bg-white rounded-lg shadow-sm p-6 flex flex-col md:flex-row gap-6 border border-gray-100">
                                {/* Profile Picture */}
                                <div className="shrink-0">
                                    {candidate.profilePic ? (
                                        <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gray-100">
                                            <img
                                                src={`${process.env.REACT_APP_API_URL}/${candidate.profilePic.replace(/\\/g, "/")}`}
                                                className="w-full h-full object-cover"
                                                style={!unlocked ? { filter: 'blur(4px)' } : {}}
                                                alt="Profile"
                                                onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${candidate.first_name}&background=random` }}
                                            />
                                        </div>
                                    ) : (
                                        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center text-gray-400">
                                            <User size={40} />
                                        </div>
                                    )}
                                </div>

                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <h3 className="text-xl font-semibold text-blue-600">
                                            {candidate.first_name} {candidate.last_name}
                                        </h3>
                                        {!unlocked && <Lock size={16} className="text-amber-500" />}
                                        {unlocked && <Unlock size={16} className="text-green-500" />}
                                    </div>

                                    <p className="text-sm text-gray-500 flex items-center gap-1 mb-4">
                                        <MapPin size={14} /> {candidate.address?.area ? `${candidate.address.area}, ` : ""}{candidate.address?.city || candidate.city || "Location N/A"}
                                    </p>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-4 text-sm text-gray-700">
                                        <p><span className="font-medium text-gray-500">Qualification:</span> {qualification}</p>
                                        <p><span className="font-medium text-gray-500">Experience:</span> {experienceVal} Years</p>
                                        <p><span className="font-medium text-gray-500">Notice Period:</span> {candidate.notice_period || "Immediate"}</p>
                                        <p><span className="font-medium text-gray-500">Current Salary:</span> {candidate.salary ? `₹${candidate.salary}` : "Not Specified"}</p>

                                    </div>
                                </div>

                                <div className="flex flex-col gap-2 justify-center min-w-[140px]">
                                    <div className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-500 rounded text-sm font-medium">
                                        <Mail size={16} /> Invited
                                    </div>

                                    {unlocked && (
                                        <Link to={`/candidate-details/${candidate._id}`} className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 text-sm font-medium transition-colors">
                                            View Profile <ExternalLink size={14} />
                                        </Link>
                                    )}
                                </div>

                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default InvitedCandidates;
